import { Layout, Menu, Dropdown, Avatar, Alert } from "antd";
import { Header } from "antd/es/layout/layout";
import Sider from "antd/es/layout/Sider";
import React, { useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate, useLocation, Navigate } from "react-router-dom";
import Logo from "./../../../assets/logo.svg";
import { usePermissions } from "../../../contexts/PermissionContext";
import {
  DashboardOutlined,
  FileDoneOutlined,
  AuditOutlined,
  LogoutOutlined,
  UserOutlined,
  SearchOutlined,
  LockOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  BankOutlined,
  ProjectOutlined,
  FolderOutlined,
  RobotOutlined,
  CommentOutlined,
  SettingOutlined,
  FileTextOutlined,
  LineChartOutlined,
  UsergroupAddOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  FileProtectOutlined,
  StarOutlined,
} from "@ant-design/icons";
import { CustomerDashboardWrapper } from "./CustomerDashboard.style";
import NotificationBell from "../../../components/NotificationBell/NotificationBell";
import CustomerOverview from "../CustomerOverview/CustomerOverview";
import FindTalents from "../FindTalents/FindTalents";
import MyJobs from "../MyJobs/MyJobs";
import SubmittedProfiles from "../SubmittedProfiles/SubmittedProfiles";
import JobDetails from "../JobDetails/JobDetails";
import PostJob from "../PostJob/PostJob";
import HiredTalents from "../HiredTalents/HiredTalents";
import TalentDetails from "../TalentDetails/TalentDetails";
import Account from "../../../components/Account/Account";
import CustomerTimesheet from "../Timesheet/Timesheet";
import FinanceManagement from "../FinanceManagement/FinanceManagement";
import CustomerJobDetails from "../ViewJobDetails/CustomerJobDetails";
import CustomerJobFullDetails from "../ViewJobDetails/CustomerJobFullDetails";
import OrganizationProfile from "../../Admin/Organization/OrganizationProfile";
import CompanyDetails from "../../Admin/Organization/CompanyDetails";
import CustomerEngagements from "../Engagements/Engagements";
import CreateEngagement from "../Engagements/CreateEngagement";
import CustomerEngagementDetails from "../Engagements/EngagementDetails";
import CustomerProjects from "../Projects/Projects";
import CreateProject from "../Projects/CreateProject";
import AIProjectAssessment from "../Projects/AIProjectAssessment";
import CreateProjectFromAssessment from "../Projects/CreateProjectFromAssessment";
import ProjectDetails from "../Projects/ProjectDetails";
import MyAssessments from "../Assessments/MyAssessments";
import AssessmentDetail from "../Assessments/AssessmentDetail";
import IssuesFeedback from "../IssuesFeedback/IssuesFeedback";
import ProfileRoles from "../../Shared/ProfileRoles/ProfileRoles";
import TeamMembers from "../../Shared/TeamMembers/TeamMembers";
import FinancialInsights from "../FinancialInsights/FinancialInsights";
import MyPartners from "../MyPartners/MyPartners";
import CustomerPartnerDetail from "../MyPartners/CustomerPartnerDetail";
import HelpCenter from "../HelpCenter/HelpCenter";
import CustomerComplianceDocs from "../ComplianceDocs/CustomerComplianceDocs";
import CustomerTalentPerformance from "../TalentPerformance/CustomerTalentPerformance";

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessMenu, loading: permissionsLoading, refreshPermissions } = usePermissions();
  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userRole = localStorage.getItem("userRole")?.toLowerCase();
    
    if (!token || userRole !== "customer") {
      navigate("/", { replace: true });
    } else {
      refreshPermissions();
    }
  }, [navigate, refreshPermissions]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/customer/profile-roles") || path.includes("/customer/team-members")) {
      setOpenKeys(["users-roles"]);
    } else if (path.includes("/customer/engagements") || path.includes("/customer/assessments") || 
               path.includes("/customer/projects") || path.includes("/customer/my-jobs") ||
               path.includes("/customer/create-engagement") || path.includes("/customer/edit-engagement") ||
               path.includes("/customer/engagement/") || path.includes("/customer/create-project") ||
               path.includes("/customer/edit-project") || path.includes("/customer/project/") ||
               path.includes("/customer/post-job") || path.includes("/customer/edit-job") ||
               path.includes("/customer/view-job") || path.includes("/customer/submitted-profiles")) {
      setOpenKeys(["requirements"]);
    } else if (path.includes("/customer/find-talents") || 
               path.includes("/customer/hired-talents") || path.includes("/customer/talent-details") ||
               path.includes("/customer/compliance-docs") || path.includes("/customer/talent-performance")) {
      setOpenKeys(["talents"]);
    } else if (path.includes("/customer/timesheet") || path.includes("/customer/invoice") || 
               path.includes("/customer/financial-insights") || path.includes("/customer/finance")) {
      setOpenKeys(["billing-finance"]);
    }
  }, [location.pathname]);

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const sideBarMenu = useMemo(() => {
    const items = [
      { 
        key: "/customer", 
        label: "Dashboard", 
        icon: <DashboardOutlined /> 
      },
      {
        key: "/customer/my-partners",
        label: "My Partners",
        icon: <GlobalOutlined />,
      },
      {
        key: "users-roles",
        label: "Users & Roles",
        icon: <TeamOutlined />,
        children: [
          {
            key: "/customer/profile-roles",
            label: "Role Management",
            icon: <SettingOutlined />,
          },
          {
            key: "/customer/team-members",
            label: "Team Members",
            icon: <UsergroupAddOutlined />,
          },
        ],
      },
      {
        key: "requirements",
        label: "Requirements",
        icon: <ProjectOutlined />,
        children: [
          {
            key: "/customer/engagements",
            label: "My Engagements",
            icon: <ProjectOutlined />,
          },
          {
            key: "/customer/assessments",
            label: "My Assessments",
            icon: <RobotOutlined />,
          },
          {
            key: "/customer/projects",
            label: "My Projects",
            icon: <FolderOutlined />,
          },
          {
            key: "/customer/my-jobs",
            label: "My Jobs",
            icon: <FileDoneOutlined />,
          },
          {
            key: "/customer/submitted-profiles",
            label: "Submitted Profiles",
            icon: <AuditOutlined />,
          },
        ],
      },
      {
        key: "talents",
        label: "Talents",
        icon: <UserOutlined />,
        children: [
          {
            key: "/customer/find-talents",
            label: "Find Talents",
            icon: <SearchOutlined />,
          },
          {
            key: "/customer/hired-talents",
            label: "Talents Hired",
            icon: <UserOutlined />,
          },
          {
            key: "/customer/compliance-docs",
            label: "Compliance Docs",
            icon: <FileProtectOutlined />,
          },
          {
            key: "/customer/talent-performance",
            label: "Performance",
            icon: <StarOutlined />,
          },
        ],
      },
      {
        key: "billing-finance",
        label: "Billing & Finance",
        icon: <DollarOutlined />,
        children: [
          {
            key: "/customer/timesheet",
            label: "Timesheet",
            icon: <ClockCircleOutlined />,
          },
          {
            key: "/customer/invoice",
            label: "Invoice",
            icon: <FileTextOutlined />,
          },
          {
            key: "/customer/financial-insights",
            label: "Financial Insights",
            icon: <LineChartOutlined />,
          },
        ],
      },
      {
        key: "/customer/issues-feedback",
        label: "Issues & Feedback",
        icon: <CommentOutlined />,
      },
      {
        key: "/customer/help-center",
        label: "Help Center",
        icon: <QuestionCircleOutlined />,
      },
    ];

    return items;
  }, []);

  const filteredSideBarMenu = useMemo(() => {
    if (permissionsLoading) return [];
    
    const filterItems = (items) => {
      return items.filter(item => {
        if (item.children) {
          const filteredChildren = item.children.filter(child => canAccessMenu(child.key));
          if (filteredChildren.length > 0) {
            item.children = filteredChildren;
            return true;
          }
          return false;
        }
        return canAccessMenu(item.key);
      });
    };
    
    return filterItems([...sideBarMenu]);
  }, [canAccessMenu, permissionsLoading, sideBarMenu]);

  const sideBarMenu2 = [
    { key: "/logout", label: "Logout", icon: <LogoutOutlined /> },
  ];

  const handleClick = (e) => {
    if (e.key === "/logout") {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userName");
      navigate("/", { replace: true });
    } else if (!e.key.startsWith("users-roles") && !e.key.startsWith("requirements") && 
               !e.key.startsWith("talents") && !e.key.startsWith("billing-finance")) {
      navigate(e.key);
    }
  };

  const profileMenuItems = [
    {
      key: 'account',
      label: 'View Account',
      icon: <UserOutlined />,
    },
    {
      key: 'company',
      label: 'View Company',
      icon: <BankOutlined />,
    },
    {
      key: 'change-password',
      label: 'Change Password',
      icon: <LockOutlined />,
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
    },
  ];

  const handleProfileMenuClick = ({ key }) => {
    if (key === 'logout') {
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userName");
      navigate("/", { replace: true });
    } else if (key === 'account' || key === 'change-password') {
      navigate("/customer/account");
    } else if (key === 'company') {
      navigate("/customer/company-details");
    }
  };

  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === "/customer" || path === "/customer/") return "/customer";
    if (path.startsWith("/customer/profile-roles")) return "/customer/profile-roles";
    if (path.startsWith("/customer/team-members")) return "/customer/team-members";
    if (path.match(/^\/customer\/my-partners\/[^/]+/)) return "/customer/my-partners";
    if (path.startsWith("/customer/my-partners")) return "/customer/my-partners";
    if (path.startsWith("/customer/engagements")) return "/customer/engagements";
    if (path.startsWith("/customer/create-engagement")) return "/customer/engagements";
    if (path.startsWith("/customer/edit-engagement")) return "/customer/engagements";
    if (path.startsWith("/customer/engagement/")) return "/customer/engagements";
    if (path.startsWith("/customer/projects")) return "/customer/projects";
    if (path.startsWith("/customer/create-project")) return "/customer/projects";
    if (path.startsWith("/customer/edit-project")) return "/customer/projects";
    if (path.startsWith("/customer/project/")) return "/customer/projects";
    if (path.startsWith("/customer/assessments")) return "/customer/assessments";
    if (path.startsWith("/customer/find-talents")) return "/customer/find-talents";
    if (path.startsWith("/customer/post-job")) return "/customer/my-jobs";
    if (path.startsWith("/customer/edit-job")) return "/customer/my-jobs";
    if (path.startsWith("/customer/view-job")) return "/customer/my-jobs";
    if (path.startsWith("/customer/my-jobs")) return "/customer/my-jobs";
    if (path.startsWith("/customer/submitted-profiles")) return "/customer/submitted-profiles";
    if (path.startsWith("/customer/hired-talents")) return "/customer/hired-talents";
    if (path.startsWith("/customer/talent-details")) return "/customer/hired-talents";
    if (path.startsWith("/customer/compliance-docs")) return "/customer/compliance-docs";
    if (path.startsWith("/customer/talent-performance")) return "/customer/talent-performance";
    if (path.startsWith("/customer/timesheet")) return "/customer/timesheet";
    if (path.startsWith("/customer/invoice")) return "/customer/invoice";
    if (path.startsWith("/customer/finance")) return "/customer/invoice";
    if (path.startsWith("/customer/financial-insights")) return "/customer/financial-insights";
    if (path.startsWith("/customer/issues-feedback")) return "/customer/issues-feedback";
    if (path.startsWith("/customer/help-center")) return "/customer/help-center";
    return "/customer";
  };

  return (
    <CustomerDashboardWrapper>
      <Layout>
        <Header
          style={{
            display: "flex",
            alignItems: "center",
            background: "#191919",
            padding: "0 24px",
            justifyContent: "space-between",
          }}
        >
          <img src={Logo} alt="logo" style={{ height: '35px', width: 'auto' }} />
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <NotificationBell />
            <Dropdown 
              menu={{ items: profileMenuItems, onClick: handleProfileMenuClick }}
              placement="bottomRight"
            >
              <Avatar
                style={{
                  backgroundColor: "#00d9a9",
                  cursor: "pointer",
                }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </div>
        </Header>
        {localStorage.getItem("userIsActive") === "false" && (
          <Alert
            message="Your account is pending approval. You can view information but cannot create or edit until an admin approves your account."
            type="warning"
            showIcon
            banner
            style={{ borderRadius: 0 }}
          />
        )}
        <Layout>
          <Sider
            width={220}
            style={{
              background: "#272727",
            }}
            className="side-navbar"
          >
            <Menu
              mode="inline"
              selectedKeys={[getSelectedKey()]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              style={{
                borderRight: 0,
              }}
              items={filteredSideBarMenu}
              onClick={handleClick}
            />

            <Menu
              mode="inline"
              style={{
                borderRight: 0,
              }}
              items={sideBarMenu2}
              onClick={handleClick}
            />
          </Sider>
          <Layout
            style={{
              padding: "30px",
              overflow: "auto",
              height: "90vh",
            }}
          >
            <Routes>
              <Route path="engagements" element={<CustomerEngagements />} />
              <Route path="create-engagement" element={<CreateEngagement />} />
              <Route path="edit-engagement/:id" element={<CreateEngagement />} />
              <Route path="engagement/:id" element={<CustomerEngagementDetails />} />
              <Route path="projects" element={<CustomerProjects />} />
              <Route path="create-project" element={<CreateProject />} />
              <Route path="create-project/ai-assessment" element={<AIProjectAssessment />} />
              <Route path="create-project/from-assessment" element={<CreateProjectFromAssessment />} />
              <Route path="edit-project/:id" element={<CreateProject />} />
              <Route path="project/:id" element={<ProjectDetails />} />
              <Route path="assessments" element={<MyAssessments />} />
              <Route path="assessments/:id" element={<AssessmentDetail />} />
              <Route path="find-talents" element={<FindTalents />} />
              <Route path="post-job" element={<PostJob />} />
              <Route path="edit-job/:jobId" element={<PostJob />} />
              <Route path="my-jobs" element={<MyJobs />} />
              <Route path="my-jobs/:jobId" element={<JobDetails />} />
              <Route path="view-job/:jobId" element={<CustomerJobDetails />} />
              <Route path="job-full-details/:jobId" element={<CustomerJobFullDetails />} />
              <Route path="submitted-profiles" element={<SubmittedProfiles />} />
              <Route path="hired-talents" element={<HiredTalents />} />
              <Route path="compliance-docs" element={<CustomerComplianceDocs />} />
              <Route path="talent-performance" element={<CustomerTalentPerformance />} />
              <Route path="talent-details/:talentId" element={<TalentDetails />} />
              <Route path="timesheet" element={<CustomerTimesheet />} />
              <Route path="invoice" element={<FinanceManagement />} />
              <Route path="finance" element={<Navigate to="/customer/invoice" replace />} />
              <Route path="financial-insights" element={<FinancialInsights />} />
              <Route path="issues-feedback" element={<IssuesFeedback />} />
              <Route path="profile-roles" element={<ProfileRoles parentRole="Customer" />} />
              <Route path="team-members" element={<TeamMembers role="customer" />} />
              <Route path="my-partners" element={<MyPartners />} />
              <Route path="my-partners/:relationId" element={<CustomerPartnerDetail />} />
              <Route path="help-center" element={<HelpCenter />} />
              <Route path="account" element={<Account role="customer" />} />
              <Route path="organization/:id" element={<OrganizationProfile />} />
              <Route path="company-details" element={<CompanyDetails />} />
              <Route path="*" element={<CustomerOverview />} />
            </Routes>
          </Layout>
        </Layout>
      </Layout>
    </CustomerDashboardWrapper>
  );
};

export default CustomerDashboard;
